import React from 'react'
import { Positions } from '../../../types'
import { ColumnDef } from '@tanstack/react-table'
import { Flex, Text, Link, useAppTheme } from '@workwhile/ui'
import { formatInTimeZone } from 'date-fns-tz'
import { formatDistanceToNowStrict, isSameYear } from 'date-fns'
import { ArrowRight } from 'lucide-react'
import { getLocalTimezone } from 'lib/time'

const boolLabel = (value: boolean) => (value ? 'Yes' : 'No')

const NameColumn: ColumnDef<Positions[number]> = {
  header: 'Name',
  accessorKey: 'name',
  minSize: 200,
  maxSize: 256,
  cell: ({ row }) => <Text>{row.original.name}</Text>,
}

const CompanyColumn: ColumnDef<Positions[number]> = {
  header: 'Company',
  accessorKey: 'company.name',
  minSize: 180,
  maxSize: 220,
  cell: ({ row }) => {
    return (
      <Text>
        {row.original.company.name} (
        <Link to={`/companies/${row.original.company.id}`}>
          {row.original.company.id}
        </Link>
        )
      </Text>
    )
  },
}

const ApprovalColumn: ColumnDef<Positions[number]> = {
  header: 'Approval',
  accessorKey: 'isApproved',
  minSize: 120,
  cell: ({ row }) => (
    <Text>{row.original.isApproved ? 'Approved' : 'Unapproved'}</Text>
  ),
}

const CompanyTypeColumn: ColumnDef<Positions[number]> = {
  header: 'Company Type',
  accessorKey: 'company.type',
  minSize: 120,
  cell: ({ row }) => <Text>{row.original.company.type}</Text>,
}

const CreatedColumn: ColumnDef<Positions[number]> = {
  header: 'Created',
  accessorKey: 'createdAt',
  minSize: 220,
  cell: ({ row }) => {
    const timeZone = getLocalTimezone()
    const isCurrentYear = isSameYear(row.original.createdAt, new Date())
    const createdOn = formatInTimeZone(
      row.original.createdAt,
      timeZone,
      isCurrentYear
        ? 'EEE MMM, dd, hh:mm a zz'
        : 'EEE MMM, dd, yyyy, hh:mm a zz'
    )
    const distance = formatDistanceToNowStrict(row.original.createdAt, {
      addSuffix: true,
    })
    return (
      <Flex as="span" flexDirection="column">
        <Text as="span">{createdOn}</Text>
        <Text as="span" color="lightText">
          ({distance})
        </Text>
      </Flex>
    )
  },
}

const HiddenColumn: ColumnDef<Positions[number]> = {
  header: 'Hidden',
  accessorKey: 'hidden',
  cell: ({ row }) => <Text>{boolLabel(row.original.hidden)}</Text>,
}

const PrescreenedColumn: ColumnDef<Positions[number]> = {
  header: 'Prescreened',
  accessorKey: 'prescreened',
  cell: ({ row }) => <Text>{boolLabel(row.original.prescreened)}</Text>,
}

const BundlingColumn: ColumnDef<Positions[number]> = {
  header: 'Bundling',
  accessorKey: 'bundling',
  cell: ({ row }) => <Text>{row.original.bundling ?? '–'}</Text>,
}

export const selectableColumns = [
  {
    id: 'name',
    label: 'Name',
    columnDef: NameColumn,
  },
  {
    id: 'company',
    label: 'Company',
    columnDef: CompanyColumn,
  },
  {
    id: 'approval',
    label: 'Approval',
    columnDef: ApprovalColumn,
  },
  {
    id: 'company-type',
    label: 'Company Type',
    columnDef: CompanyTypeColumn,
  },
  {
    id: 'created',
    label: 'Created',
    columnDef: CreatedColumn,
  },
  {
    id: 'hidden',
    label: 'Hidden',
    columnDef: HiddenColumn,
  },
  {
    id: 'prescreened',
    label: 'Prescreened',
    columnDef: PrescreenedColumn,
  },
  {
    id: 'bundling',
    label: 'Bundling',
    columnDef: BundlingColumn,
  },
] as const

export type SelectableColumns = (typeof selectableColumns)[number]['id']

/**
 *
 *
 *  Non-selectable columns go here. They're exported out to be used directly in the table.
 *
 *
 */

export const IDColumn: ColumnDef<Positions[number]> = {
  header: 'ID',
  accessorKey: 'id',
  minSize: 50,
  cell: ({ row }) => <Text>{row.original.id}</Text>,
}

export const DetailColumn: ColumnDef<Positions[number]> = {
  header: 'Detail',
  size: 85,
  cell: ({ row }) => {
    const { colors } = useAppTheme()
    return (
      <Link to={`/positions/${row.original.id}`}>
        View <ArrowRight size={14} color={colors.primary} />
      </Link>
    )
  },
}
